import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {ListResponse} from '../../interface/response/list-response';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {Contract} from '../../interface/contract';
import {ContractService} from '../../services/contract.service';
import {Partner} from "../../interface/partner";
import {COMPANY_ASF, COMPANY_ASF_PREMIUM} from "../../services/dict.service";

@Component({
  selector: 'app-dict-all-contracts',
  templateUrl: './dict-all-contracts.component.html',
  styleUrls: ['./dict-all-contracts.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictAllContractsComponent)}
  ]
})
export class DictAllContractsComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() invoiceBlock: boolean;
  @Input() required = false;
  @Input() filterPartnership = false;
  @Input() filterRegistrationType = false;
  @Input() showOwnerName = false;
  @Input() showStatus = false;
  @Input() companyId: number = null;
  @Input() partnershipContracts = false;
  @Input() partnerContractsChange = false;
  @Input() partnerContractsChangeOfwca = false;
  @Input() addInvoiceContract = false;
  @Input() showElements = 5;
  @Input() addInvoiceContractTypes = false;
  @Input() invoiceGeneratorContractTypes = false;
  @Output() selectedContract: EventEmitter<Contract> = new EventEmitter<Contract>();
  @Output() selectedPartner: EventEmitter<Partner> = new EventEmitter<Partner>();

  externalControl = true;
  disabled = false;
  items: Contract[] = [];
  value?: string = null;
  contractFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<Contract>> = new ReplaySubject<ListResponse<Contract>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private contractService: ContractService,
    private fb: FormBuilder,
  ) {
    this.contractFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((contract: Contract) => {
            if (contract.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.contractService.loadNotCountedList(0, this.showElements, {
              uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.contractFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.contractService.loadNotCountedList(0, this.showElements, {
            context: val,
            onlyPartnership: this.filterPartnership ? true : null,
            registrationTypes: this.filterRegistrationType ? true : null,
            company: this.companyId === null ? null : this.companyId,
            partnershipAllContractsSearch: this.partnershipContracts === null ? null : this.partnershipContracts,
            partnershipContractTypesChange: this.partnerContractsChange === null ? null : this.partnerContractsChange,
            partnershipContractTypesChangeOfwca: this.partnerContractsChangeOfwca === null ? null : this.partnerContractsChangeOfwca,
            addInvoiceContract: this.addInvoiceContract === null ? null : this.addInvoiceContract,
            addInvoiceContractTypes: this.addInvoiceContractTypes === null ? null : this.addInvoiceContractTypes,
            invoiceGeneratorContractTypes: this.invoiceGeneratorContractTypes === null ? null : this.invoiceGeneratorContractTypes,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.contractService.loadNotCountedList(0, this.showElements, {
            uuid: this.control.value
          }, this.itemsSubject);
        }
      });


    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Contract>) => {
        this.items = result.items;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((contract: Contract) => {
      if (contract.uuid === e.value) {
        this.selectedContract.emit(contract);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }
  onSelectionChangePartner(e: MatSelectChange): void {
    this.items.forEach((contract: Contract) => {
      if (contract.uuid === e.value) {
        this.selectedPartner.emit(contract.partner);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
